if (!customElements.get('bundle-product-card')) {
  customElements.define(
    'bundle-product-card',
    class BundleProductCard extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound === '1') return;
        this.dataset.bound = '1';
        this.setAttribute('role', 'button');
        this.setAttribute('tabindex', '0');
        this.setAttribute('aria-pressed', 'false');
        this.addEventListener('click', this.onToggle);
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.onToggle();
          }
        });
      }

      onToggle = () => {
        if (this.getAttribute('aria-disabled') === 'true') return;
        const builder = this.closest('bundle-builder');
        if (!builder) return;
        const handle = this.dataset.productHandle;
        const alreadySelected = this.getAttribute('aria-pressed') === 'true';
        if (alreadySelected) {
          builder.deselect(handle);
        } else {
          builder.select(handle, this);
        }
      };
    }
  );
}

if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound === '1') return;
        this.dataset.bound = '1';

        this.tiers = Array.from(this.querySelectorAll('[data-bundle-tier]')).map((el) => ({
          id: el.dataset.bundleTier,
          size: el.dataset.bundleSize,
          required: parseInt(el.dataset.bundleRequired, 10) || 3,
          bundleId: el.dataset.bundleProductId,
          bundleSlug: el.dataset.bundleSlug,
          tag: el.dataset.bundleTag || '',
          panel: el,
          tray: this.querySelector(`[data-tray-for="${el.dataset.bundleTier}"]`),
        }));

        if (this.tiers.length === 0) return;

        this.activeTier = this.tiers[0];
        this.selections = new Map(this.tiers.map((t) => [t.id, []]));

        this.setupTabs();
        this.annotateTabCounts();
        this.applyDeeplink();
        this.refreshAndSort();
        this.renderTray();

        // Re-run hydration + sort when the page is restored from the back/forward cache —
        // localStorage may have been updated on the page the user navigated to.
        this.onPageShow = (event) => {
          if (event.persisted) this.refreshAndSort();
        };
        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      }

      async refreshAndSort() {
        try {
          await this.hydrateRecentlyViewed();
        } catch (err) {
          console.error('[bundle-builder] hydrate failed', err);
        }
        try {
          await this.sortByPersonalScore();
        } catch (err) {
          console.error('[bundle-builder] sort failed', err);
        }
      }

      async hydrateRecentlyViewed() {
        let recent = [];
        try {
          recent = JSON.parse(localStorage.getItem('yp_recent') || '[]');
        } catch (e) { return; }
        if (!recent.length) return;

        const fetched = new Map();

        for (const tier of this.tiers) {
          const grid = tier.panel.querySelector('[data-bundle-grid]');
          if (!grid) continue;

          const presentHandles = new Set(
            Array.from(grid.querySelectorAll('bundle-product-card')).map((c) => c.dataset.productHandle)
          );
          const missing = recent.filter((r) => r && r.handle && !presentHandles.has(r.handle));
          if (missing.length === 0) continue;

          // Fetch missing products in parallel; cap to 10 to avoid hammering
          const lookups = await Promise.all(
            missing.slice(0, 10).map(async (r) => {
              if (fetched.has(r.handle)) return fetched.get(r.handle);
              try {
                const res = await fetch(`/products/${encodeURIComponent(r.handle)}.js`);
                if (!res.ok) return null;
                const product = await res.json();
                fetched.set(r.handle, product);
                return product;
              } catch (e) {
                return null;
              }
            })
          );

          // Prepend cards (so they're at the top of the DOM order; sort will resolve final position)
          // Filter out recently-viewed products that aren't bundle-eligible for this tier (e.g. frames, gift cards).
          lookups.filter(Boolean).forEach((product) => {
            if (tier.tag && !(product.tags || []).includes(tier.tag)) return;
            const card = this.buildCard(product, tier.size);
            if (card) grid.prepend(card);
          });
        }
      }

      buildCard(product, size) {
        let variantA4 = '';
        let variantA3 = '';
        let variantA2 = '';
        let availableForSize = false;
        for (const v of product.variants || []) {
          const opts = [v.option1, v.option2, v.option3].filter(Boolean);
          for (const opt of opts) {
            if (opt.includes('A4')) {
              variantA4 = v.id;
              if (size === 'A4' && v.available) availableForSize = true;
            }
            if (opt.includes('A3')) {
              variantA3 = v.id;
              if (size === 'A3' && v.available) availableForSize = true;
            }
            if (opt.includes('A2')) {
              variantA2 = v.id;
              if (size === 'A2' && v.available) availableForSize = true;
            }
          }
        }

        const card = document.createElement('bundle-product-card');
        card.dataset.productHandle = product.handle;
        card.dataset.productTitle = product.title || '';
        card.dataset.variantA4 = variantA4;
        card.dataset.variantA3 = variantA3;
        card.dataset.variantA2 = variantA2;
        const ts = product.published_at ? Math.floor(new Date(product.published_at).getTime() / 1000) : 0;
        card.dataset.publishedTs = String(ts);
        card.dataset.bestSeller = '0';
        card.dataset.newArrival = '0';
        card.dataset.previouslyOrdered = '0';
        card.dataset.tags = (product.tags || []).join(',');
        if (!availableForSize) card.setAttribute('aria-disabled', 'true');

        const img = product.featured_image
          ? `<img src="${product.featured_image}" alt="${(product.title || '').replace(/"/g, '&quot;')}" loading="lazy" width="400" height="400">`
          : '';
        const soldoutLabel = !availableForSize
          ? `<p class="bundle-card__soldout">${variantA4 || variantA3 || variantA2 ? 'Sold out in this size' : 'Not in ' + size}</p>`
          : '';

        card.innerHTML =
          '<div class="bundle-card__media">' +
          img +
          '<span class="bundle-card__badge" hidden></span>' +
          '<span class="bundle-card__check" aria-hidden="true">' +
          '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' +
          '<polyline points="4 10 8 14 16 6"></polyline>' +
          '</svg>' +
          '</span>' +
          '</div>' +
          '<div class="bundle-card__info">' +
          `<p class="bundle-card__title">${(product.title || '').replace(/</g, '&lt;')}</p>` +
          soldoutLabel +
          '</div>';
        return card;
      }

      annotateTabCounts() {
        this.tiers.forEach((tier) => {
          const tab = this.querySelector(`[data-bundle-tab="${tier.id}"]`);
          const grid = tier.panel.querySelector('[data-bundle-grid]');
          const count = grid ? parseInt(grid.dataset.tileCount || '0', 10) : 0;
          if (!tab || count <= 0) return;
          if (tab.querySelector('.bundle-builder__tab-count')) return;
          const pill = document.createElement('span');
          pill.className = 'bundle-builder__tab-count';
          pill.textContent = `${count}`;
          pill.setAttribute('aria-label', `${count} prints available`);
          tab.appendChild(pill);
        });
      }

      applyDeeplink() {
        // ?tier=<block-id> or ?bundle=<bundle-slug>
        const params = new URLSearchParams(window.location.search);
        const wantedTier = params.get('tier');
        const wantedSlug = params.get('bundle');
        let target = null;
        if (wantedTier) {
          target = this.tiers.find((t) => t.id === wantedTier);
        }
        if (!target && wantedSlug) {
          target = this.tiers.find((t) => t.bundleSlug === wantedSlug);
        }
        if (target && target.id !== this.activeTier.id) {
          this.switchTier(target.id);
        }
      }

      setupTabs() {
        this.querySelectorAll('[data-bundle-tab]').forEach((tab) => {
          tab.addEventListener('click', () => {
            const tierId = tab.dataset.bundleTab;
            this.switchTier(tierId);
          });
        });
      }

      switchTier(tierId) {
        const tier = this.tiers.find((t) => t.id === tierId);
        if (!tier) return;
        this.activeTier = tier;
        this.querySelectorAll('[data-bundle-tab]').forEach((tab) => {
          tab.setAttribute('aria-selected', tab.dataset.bundleTab === tierId ? 'true' : 'false');
        });
        this.tiers.forEach((t) => {
          t.panel.setAttribute('data-active', t.id === tierId ? 'true' : 'false');
          if (t.tray) t.tray.setAttribute('data-active', t.id === tierId ? 'true' : 'false');
        });
        // Update URL without full reload — preserves back-button history
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('bundle', tier.bundleSlug);
          url.searchParams.delete('tier');
          window.history.replaceState({}, '', url);
        } catch (e) {}
      }

      async sortByPersonalScore() {
        // 1. Recently-viewed handles → rank map
        let recent = [];
        try {
          recent = JSON.parse(localStorage.getItem('yp_recent') || '[]');
        } catch (e) {}
        const recentRank = new Map(recent.map((r, i) => [r.handle, recent.length - i]));

        // 1b. Reset badges on every sort so re-runs (hydration, BFCache) don't leave stale labels
        this.tiers.forEach((t) => {
          t.panel.querySelectorAll('.bundle-card__badge').forEach((b) => {
            b.textContent = '';
            b.hidden = true;
            delete b.dataset.kind;
          });
        });

        // 2. Cart contents — best-effort fetch, tolerate failure
        let inCartHandles = new Set();
        try {
          const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          if (res.ok) {
            const cart = await res.json();
            inCartHandles = new Set((cart.items || []).map((it) => it.handle));
          }
        } catch (_) {}

        // 3. Build affinity tag set from recently-viewed tiles already on the page
        const recentTags = new Set();
        this.tiers.forEach((t) => {
          t.panel.querySelectorAll('bundle-product-card').forEach((card) => {
            if (recentRank.has(card.dataset.productHandle)) {
              (card.dataset.tags || '').split(',').forEach((tag) => {
                const trimmed = tag.trim();
                if (trimmed) recentTags.add(trimmed);
              });
            }
          });
        });

        const W = { RECENT: 100, REPEAT: 60, BESTSELLER: 40, NEW: 25, AFFINITY: 15, JITTER: 5, CART: 200 };
        const BADGE_LABELS = {
          recent: 'Just viewed',
          repeat: "You've bought this",
          bestseller: 'Best seller',
          new: 'New',
          picked: 'Picked for you',
        };

        // 4. Score and sort each tier independently
        this.tiers.forEach((tier) => {
          const grid = tier.panel.querySelector('[data-bundle-grid]');
          if (!grid) return;
          const cards = Array.from(grid.querySelectorAll('bundle-product-card'));

          cards
            .map((card) => {
              const handle = card.dataset.productHandle;
              const tags = (card.dataset.tags || '').split(',').map((t) => t.trim()).filter(Boolean);
              const tagOverlap = tags.reduce((acc, t) => acc + (recentTags.has(t) ? 1 : 0), 0);
              const isRecent = recentRank.has(handle);
              const isRepeat = card.dataset.previouslyOrdered === '1';
              const isBest = card.dataset.bestSeller === '1';
              const isNew = card.dataset.newArrival === '1';
              const inCart = inCartHandles.has(handle);

              const score =
                W.RECENT * (recentRank.get(handle) || 0) +
                W.REPEAT * (isRepeat ? 1 : 0) +
                W.BESTSELLER * (isBest ? 1 : 0) +
                W.NEW * (isNew ? 1 : 0) +
                W.AFFINITY * tagOverlap +
                W.JITTER * Math.random() -
                W.CART * (inCart ? 1 : 0);

              // Only surface badges for personal signals — not merchandising flags
              let badgeKind = null;
              if (isRecent) badgeKind = 'recent';
              else if (isRepeat) badgeKind = 'repeat';

              return { card, score, badgeKind };
            })
            .sort((a, b) => b.score - a.score)
            .forEach((item, idx) => {
              if (idx < 3 && item.badgeKind) {
                const badge = item.card.querySelector('.bundle-card__badge');
                if (badge && !badge.textContent) {
                  badge.textContent = BADGE_LABELS[item.badgeKind];
                  badge.dataset.kind = item.badgeKind;
                  badge.hidden = false;
                }
              }
              grid.appendChild(item.card);
            });
        });
      }

      getCard(handle, tier = this.activeTier) {
        return tier.panel.querySelector(`bundle-product-card[data-product-handle="${CSS.escape(handle)}"]`);
      }

      select(handle, cardEl) {
        const tier = this.activeTier;
        const list = this.selections.get(tier.id);
        if (list.length >= tier.required) return;
        if (list.some((s) => s.handle === handle)) return;
        const variantId = cardEl.dataset[`variant${tier.size}`];
        if (!variantId) return;
        const image = cardEl.querySelector('img')?.src || '';
        const title = cardEl.dataset.productTitle || '';
        list.push({ handle, variantId, image, title });
        cardEl.setAttribute('aria-pressed', 'true');
        this.renderTray();
      }

      deselect(handle) {
        const tier = this.activeTier;
        const list = this.selections.get(tier.id);
        const idx = list.findIndex((s) => s.handle === handle);
        if (idx === -1) return;
        list.splice(idx, 1);
        const card = this.getCard(handle);
        if (card) card.setAttribute('aria-pressed', 'false');
        this.renderTray();
      }

      renderTray() {
        this.tiers.forEach((tier) => {
          if (!tier.tray) return;
          const list = this.selections.get(tier.id);
          const slotsRoot = tier.tray.querySelector('[data-tray-slots]');
          const countEl = tier.tray.querySelector('[data-tray-count]');
          const btn = tier.tray.querySelector('[data-tray-cta]');

          if (slotsRoot) {
            slotsRoot.innerHTML = '';
            for (let i = 0; i < tier.required; i++) {
              const sel = list[i];
              const slot = document.createElement('div');
              slot.className = 'bundle-builder__slot' + (sel ? ' bundle-builder__slot--filled' : '');
              if (sel) {
                const img = document.createElement('img');
                img.src = sel.image;
                img.alt = sel.title;
                img.loading = 'lazy';
                slot.appendChild(img);
                const remove = document.createElement('button');
                remove.type = 'button';
                remove.className = 'bundle-builder__slot-remove';
                remove.setAttribute('aria-label', 'Remove ' + sel.title);
                remove.textContent = '×';
                remove.addEventListener('click', (e) => {
                  e.stopPropagation();
                  this.deselect(sel.handle);
                });
                slot.appendChild(remove);
              } else {
                slot.textContent = '+';
              }
              slotsRoot.appendChild(slot);
            }
          }

          if (countEl) {
            countEl.textContent = `${list.length}/${tier.required}`;
          }

          if (btn) {
            const ready = list.length === tier.required;
            btn.setAttribute('aria-disabled', ready ? 'false' : 'true');
            if (!btn.dataset.bound) {
              btn.dataset.bound = '1';
              btn.addEventListener('mousedown', (e) => {
                if (btn.getAttribute('aria-disabled') === 'true') return;
                e.preventDefault();
                this.addBundleToCart(tier);
              });
            }
          }
        });
      }

      async addBundleToCart(tier) {
        const list = this.selections.get(tier.id);
        if (list.length !== tier.required) return;

        const btn = tier.tray.querySelector('[data-tray-cta]');
        const btnSpan = btn?.querySelector('span');
        const originalText = btnSpan?.textContent;
        if (btnSpan) btnSpan.textContent = 'Adding…';
        btn?.setAttribute('aria-disabled', 'true');

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sections = cart?.getSectionsToRender?.().map((s) => s.id).join(',');

        const payload = {
          items: list.map((s) => ({
            id: Number(s.variantId),
            quantity: 1,
            properties: {
              _bundle: tier.bundleSlug,
            },
          })),
        };
        if (sections) {
          payload.sections = sections;
          payload.sections_url = window.location.pathname;
        }

        try {
          const res = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (!res.ok || data.status) {
            throw new Error(data.description || data.message || 'Add to cart failed');
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder', cartData: data });
          }
          if (cart?.renderContents) {
            cart.renderContents(data);
          }
          if (cart?.open) {
            cart.open();
          }

          this.resetTier(tier);
        } catch (err) {
          console.error('[bundle-builder]', err);
          alert('Sorry, we couldn’t add this bundle. Please try again.');
        } finally {
          if (btnSpan && originalText) btnSpan.textContent = originalText;
          this.renderTray();
        }
      }

      resetTier(tier) {
        this.selections.set(tier.id, []);
        tier.panel.querySelectorAll('bundle-product-card[aria-pressed="true"]').forEach((c) => {
          c.setAttribute('aria-pressed', 'false');
        });
      }
    }
  );
}
